import type {
  ActivityEntry,
  HttpActivity,
  RouteActivity,
  UiActivity,
  WidgetLoadActivity
} from '@/lib/widget-activity';

/**
 * Bytes the timeline may take in the record body. The rest of the record, two attributes
 * at their 512-char cap included, stays under 4 KB, so the request fits nginx's 16 KB
 * location cap with room to spare (`docker-entrypoint.sh`).
 */
export const MAX_TIMELINE_BYTES = 8 * 1024;

function describeUi(entry: UiActivity): string {
  const who = entry.scope === undefined ? 'host ' : '';
  const key = entry.key === undefined ? '' : ` ${entry.key}`;
  const run = entry.count > 1 ? ` ×${entry.count}` : '';
  return `${who}${entry.event}${key} ${entry.target}${run}`;
}

function describeHttp(entry: HttpActivity): string {
  const outcome = entry.status ?? entry.error ?? 'ok';
  const label = entry.label === undefined ? '' : ` (${entry.label})`;
  return `${entry.method} ${entry.url} → ${outcome} in ${entry.durationMs} ms${label}`;
}

function describeLoad(entry: WidgetLoadActivity): string {
  const where = entry.stage === undefined ? '' : ` at ${entry.stage}`;
  const took = entry.elapsedMs === undefined ? '' : ` in ${entry.elapsedMs} ms`;
  return `${entry.scope} load ${entry.event}${where}${took}`;
}

function describeRoute(entry: RouteActivity): string {
  return `route ${entry.path}`;
}

/** One line a person reads: `click button[data-testid=save]`, `GET /api/tasks → 500 in 1200 ms`. */
export function describeActivity(entry: ActivityEntry): string {
  switch (entry.kind) {
    case 'ui':
      return describeUi(entry);
    case 'http':
      return describeHttp(entry);
    case 'load':
      return describeLoad(entry);
    case 'route':
      return describeRoute(entry);
  }
}

/** `-12.4s`: how long before the failure the entry happened, as a reader scans a timeline. */
export function relativeTime(time: number, failureTime: number): string {
  const seconds = (time - failureTime) / 1000;
  return `${seconds > 0 ? '+' : ''}${seconds.toFixed(1)}s`;
}

/** UTF-8 bytes, what the wire and nginx count. */
function byteLength(text: string): number {
  return typeof TextEncoder === 'undefined' ? text.length : new TextEncoder().encode(text).length;
}

/**
 * The run-up to a failure as lines, oldest first, each with its distance to the failure.
 * When the lines run past `maxBytes`, the oldest are left out: the last moments matter most.
 */
export function activityTimeline(
  entries: readonly ActivityEntry[],
  failureTime: number,
  maxBytes: number = MAX_TIMELINE_BYTES
): string {
  const lines = entries.map(
    (entry) => `${relativeTime(entry.time, failureTime)} ${describeActivity(entry)}`
  );
  const kept: string[] = [];
  let bytes = 0;
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const cost = byteLength(lines[index]) + (kept.length === 0 ? 0 : 1);
    if (bytes + cost > maxBytes) {
      break;
    }
    kept.unshift(lines[index]);
    bytes += cost;
  }
  return kept.join('\n');
}
